import {User as FirebaseUser} from 'firebase/auth';
import axios, {AxiosError} from 'axios';
import {
  ApiRequestPaymentGetContractBlueprintsV3,
  ApiResponsePaymentGetContractBlueprintsV3,
} from '../../types/api/getContractBlueprints';
import {EnvConfig} from '../config/envConfig.interface';

import {User} from './user';
import {
  ApiRequestFinishLogin,
  ApiResponseFinishLogin,
} from '../../types/api/finishLogin';
import {PaymentAction, PaymentData, ResultCode} from '@adyen/adyen-web';

export enum Platform {
  alexa = 'alexa',
  googleAssistant = 'googleAssistant',
  chromecast = 'chromecast',
  appleTv = 'appleTv',
  siri = 'siri',
  android = 'android',
  ios = 'ios',
  web = 'web',
  undefined = 'platform_undefined',
}

export enum ApiErrorCode {
  general_internalError = 'general_internalError',
  general_invalidUser = 'general_invalidUser',
}

export interface Localized<T> {
  de_DE: T;
  default: T;
}

export type AdyenDropinData = Record<string, unknown>;

export interface ApiContractBlueprintCalculations {
  paidMonthly: boolean;
  paidWeekly: boolean;
  paidYearly: boolean;
  pricePerMonth: number;
  pricePerWeek: number;
  discountPricePerMonth: number | null;
  totalMonths: number;
  totalPrice: number;
  totalWeeksPlus: number;
}

export interface ApiContractBlueprint {
  id: string;
  name?: Localized<string>;
  periodWeeks?: number;
  periodMonths?: number;
  periodCount: number;
  periodPrice: number;
  firstPeriodWeeks?: number;
  firstPeriodMonths?: number;
  firstPeriodPrice?: number;
  cancelOnFirstPeriodRenewOff?: boolean;
  currency: string;
  renew: boolean;
  isRecurring: boolean;
  tax: {
    rate: number;
  };
  calculations: ApiContractBlueprintCalculations;
  badge?: string;
  urlIdentifier?: string;
  perkBase?: boolean;
  highlight?: boolean;
  discountNormalPrice?: number;

  sortIndex: number;
}

export interface ApiContractBlueprintGroup {
  id: string;
  name: Localized<string>;
  description: Localized<string>;
  features: Record<string, unknown>;
  selectableFeatureSets?: ApiContractBpGroupSelectableFeatureSet[];
  preselectedFeatureSet?: string;
  productHierarchy?: number;
  urlIdentifier?: string;
  contractBlueprints: ApiContractBlueprint[];
}

export interface ApiContractBpGroupSelectableFeatureSet {
  id: string;
  name: string;
  features: Record<string, unknown>;
}

export interface ApiResponse {
  success: boolean;
  message: string;
  message_localized: string;
  version: number;
  execution_id: string;
}

export interface ApiServiceResponse<TResponse extends ApiResponse> {
  userId?: string;
  response: TResponse;
}

export interface ApiResponseUserTokenZendeskIdentification extends ApiResponse {
  jwt: string;
}

export interface ApiResponsePaymentGetContractBlueprintsV2 extends ApiResponse {
  contractBlueprintGroups: ApiContractBlueprintGroup[];
  discount?: {
    title: unknown;
    dueDate: string;
  };
}

export interface ApiRequestPaymentGetContractBlueprintsV2 {
  platform: string;
  apiLanguage: string;
  blueprintVariant: string;
  languageId: string;
  provider: string[];
  version: number;
  allowAnonymous: boolean;
}

export interface ApiResponsePaymentDropinPaymentMethodsCurrentSubscription {
  contractId: string;
  endDate: string;
  periodPrice: number;
  currency: string;
  periodMonths: number;
  periodWeeks: number;

  bpGroup: {
    id: string;
    name: unknown;
    description: unknown;
  };
  selectedFeatureSet?: {
    id: string;
    name: string;
  };

  calculations: {
    pricePerMonth: number;
  };
}

export interface ApiResponsePaymentDropinPaymentMethods extends ApiResponse {
  handler: 'dropin' | 'upgradeForm' | 'downgradeForm';
  /**
   * Payment methods.
   * Only if 'handler:dropin'
   */
  paymentMethods?: {
    paymentMethods: {
      name: string;
      type: string;
      configuration?: object;
      brands?: string[];
    }[];
  };
  amount?: {
    value: number;
    currency: string;
  };
  merchantAccount?: string;
  currentSubscription?: ApiResponsePaymentDropinPaymentMethodsCurrentSubscription; // only if handler = upgradeForm || downgradeForm
  adyenTestEnvironment: boolean;
}

export interface ApiResponsePaymentDropinPay extends ApiResponse {
  action?: PaymentAction;
  refusalReason?: unknown;
  startsAt?: string;
  contractId?: string;
  amount?: {
    currency: string;
    value: number;
  };
  blueprintGroup?: string;
  blueprint?: string;
  adyenTestEnvironment: boolean;
  resultCode: ResultCode;
}

export interface ApiResponsePaymentDropinPaymentDetails extends ApiResponse {
  action?: PaymentAction;
  refusalReason?: unknown;
  contractId?: string;
  amount?: {
    currency: string;
    value: number;
  };
  blueprintGroup?: string;
  blueprint?: string;
  startsAt?: string;
  adyenTestEnvironment: boolean;
  resultCode: ResultCode;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ApiRequest {}

export interface ApiRequestPaymentDropinPaymentMethods extends ApiRequest {
  blueprintVariant: string;
  blueprintGroup: string;
  blueprintId: string;
  periodPrice: number;
  channel: 'Web' | 'Web-test';
}

export interface ApiRequestPaymentDropinPay extends ApiRequest {
  blueprintVariant: string;
  blueprintGroup: string;
  blueprintId: string;
  periodPrice: number;
  channel: 'Web' | 'Web-test';
  selectedFeatureSet: string | null;
  invoiceCountryCode: string;
  collectedContacts?: {
    type: string;
    value: string;
  }[];
  returnUrl: string;
  shopperLocale: string;
  browserInfo: {
    userAgent: string;
    language: string;
    javaEnabled: boolean;
    screenHeight: number;
    screenWidth: number;
    colorDepth: number;
    timeZoneOffset: number;
  };
  dropinData: PaymentData;
}

export interface ApiRequestPaymentDropinPaymentDetails extends ApiRequest {
  blueprintVariant: string;
  channel: 'Web' | 'Web-test';
  dropinData: AdyenDropinData;
  requestContractDetails?: boolean;
  contractId?: string;
}

export type PayConfigPaymentmethod =
  | {
      type: 'card';
      paymentMethodId: string;
    }
  | {
      type: 'sofort';
      holderName: string;
    };

export type ErrorCode =
  | 'generic/generic'
  | 'general/internalError'
  | 'general/invalidUser'
  | 'payment/unavailable' // generic unavailable state
  | 'payment/unavailableVariant' // unavailable due to variant issues
  | 'payment_unavailableVariant'
  | 'payment/unavailableAccount' // unavailable to the users account
  | 'payment/unavailableAccountMaxPurchasesUser' // max purchases per user reached on bp
  | 'payment/unavailableAlreadySubscription' // user has already a subscription
  | 'payment/unavailableCountry' // bp not available in users country
  | 'payment/locationEvidenceMissmatch' // location evidences could not be matched (at least 2 must be the same)
  | 'payment/unsupportedTaxCountry' // tax country is not supported
  | 'payment/refreshPage' // user should refresh page and try again (offer might have changed)
  | 'payment/paymentProvider' // error while communicating with payment provider
  | 'payment/unsupportedPaymentMethod' // the payment method is not supported
  | 'payment/payUnsuccessful' // generic unsuccessful state
  | 'payment/payRefused';

export class ApiError extends Error {
  public readonly isApiError = true;
  public readonly code: ErrorCode;
  //public readonly message;
  public readonly executionId: string;
  public readonly data: Record<string, unknown>;

  constructor(
    code: ErrorCode,
    message: string,
    executionId: string,
    additionalData = {},
  ) {
    super(message);
    this.code = code;
    this.executionId = executionId;
    this.data = additionalData;
  }
}

export interface IApiService {
  payment: {
    getContractBlueprints: (
      data: ApiRequestPaymentGetContractBlueprintsV3,
      showNetworkErrorDialog: boolean,
    ) => Promise<ApiServiceResponse<ApiResponsePaymentGetContractBlueprintsV3>>;
    dropin: {
      paymentMethods: (
        data: ApiRequestPaymentDropinPaymentMethods,
        showNetworkErrorDialog: boolean,
      ) => Promise<ApiServiceResponse<ApiResponsePaymentDropinPaymentMethods>>;
      pay: (
        data: ApiRequestPaymentDropinPay,
        showNetworkErrorDialog: boolean,
      ) => Promise<ApiServiceResponse<ApiResponsePaymentDropinPay>>;
      paymentDetails: (
        data: ApiRequestPaymentDropinPaymentDetails,
        showNetworkErrorDialog: boolean,
      ) => Promise<ApiServiceResponse<ApiResponsePaymentDropinPaymentDetails>>;
      keepAwake: (
        data: ApiRequest,
        showNetworkErrorDialog: boolean,
      ) => Promise<ApiServiceResponse<ApiResponse>>;
    };
  };
  userTokens: {
    zendeskIdentification: (
      data: ApiRequest,
      showNetworkErrorDialog: boolean,
    ) => Promise<ApiServiceResponse<ApiResponseUserTokenZendeskIdentification>>;
  };
  user: {
    finishLogin: (params: {
      data: ApiRequestFinishLogin;
      showNetworkErrorDialog?: boolean;
    }) => Promise<ApiServiceResponse<ApiResponseFinishLogin>>;
  };
}

export class ApiService implements IApiService {
  constructor(protected configuration: EnvConfig) {
    return;
  }

  private async post<TResponse extends ApiResponse>(
    endpoint: string,
    {
      data = {},
      showNetworkErrorDialog = true,
      includeLanguage = true,
      customIdToken,
      withCredentials = false,
    }: {
      data?: Record<string, unknown>;
      showNetworkErrorDialog?: boolean;
      includeLanguage?: boolean;
      customIdToken?: string;
      withCredentials?: boolean;
    },
  ): Promise<ApiServiceResponse<TResponse>> {
    const sendData: Record<string, unknown> = {
      platform: Platform.web,
      ...data,
    };

    if (includeLanguage) {
      const environment = this.configuration;
      sendData['apiLanguage'] = environment.language;
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    let user: FirebaseUser | null = null;

    if (customIdToken) {
      headers.Authorization = `Bearer ${customIdToken}`;
    } else {
      user = await User.instance.currentFirebaseAuthUser();
      if (user) {
        const bearer = await user.getIdToken();
        if (bearer) {
          headers.Authorization = `Bearer ${bearer}`;
        }
      }
    }

    try {
      const res = await axios.post<TResponse>(
        this.configuration.apiBaseUrl + '/' + endpoint,
        sendData,
        {
          headers,
          withCredentials,
        },
      );

      return {response: res.data, userId: user?.uid};
    } catch (err) {
      if (err instanceof Error) {
        if (axios.isAxiosError(err)) {
          const aErr = err as AxiosError<TResponse>;

          if (aErr.response?.data?.success === false) {
            const origData = aErr.response.data;
            const data = {...origData} as Record<string, unknown>;
            //delete data.success;
            delete data.version;
            data.message;
            data.message_localized;
            delete data.execution_id;
            const apiError = new ApiError(
              data.errorCode
                ? (data.errorCode as ErrorCode)
                : 'generic/generic',
              origData.message_localized,
              origData.execution_id,
              data,
            );
            throw apiError;
          }
        }
        //window.alert('UNKNOWN API REQUEST ERROR');
        console.error('UNKNOWN API REQUEST ERROR');
        console.error(err);

        if (showNetworkErrorDialog) {
          //this.dialog.open(DialogApiErrorComponent);
        }
        throw new Error(err.message);
      }
      throw err;
    }
  }

  public payment /*: ApiEndpointRack*/ = {
    getContractBlueprints: async (
      data: ApiRequestPaymentGetContractBlueprintsV3,
      showNetworkErrorDialog = true,
    ): Promise<
      ApiServiceResponse<ApiResponsePaymentGetContractBlueprintsV3>
    > => {
      return (await this.post('payment/getContractBlueprints', {
        data: {...data},
        showNetworkErrorDialog,
      })) as ApiServiceResponse<ApiResponsePaymentGetContractBlueprintsV3>;
    },
    dropin: {
      paymentMethods: (
        data: ApiRequestPaymentDropinPaymentMethods,
        showNetworkErrorDialog = true,
      ): Promise<
        ApiServiceResponse<ApiResponsePaymentDropinPaymentMethods>
      > => {
        return this.post<ApiResponsePaymentDropinPaymentMethods>(
          'payment/dropin/paymentMethods',
          {
            data: {...data},
            showNetworkErrorDialog,
          },
        );
      },
      pay: (
        data: ApiRequestPaymentDropinPay,
        showNetworkErrorDialog = true,
      ): Promise<ApiServiceResponse<ApiResponsePaymentDropinPay>> => {
        return this.post<ApiResponsePaymentDropinPay>('payment/dropin/pay', {
          data: {...data},
          showNetworkErrorDialog,
          // Enable credentials, so that we can extract additional data
          // from Cookies in our API e.g. UTM parameters
          withCredentials: true,
        });
      },
      paymentDetails: (
        data: ApiRequestPaymentDropinPaymentDetails,
        showNetworkErrorDialog = true,
      ): Promise<
        ApiServiceResponse<ApiResponsePaymentDropinPaymentDetails>
      > => {
        return this.post<ApiResponsePaymentDropinPaymentDetails>(
          'payment/dropin/paymentDetails',
          {
            data: {...data},
            showNetworkErrorDialog,
          },
        );
      },
      keepAwake: (
        data: ApiRequest = {},
        showNetworkErrorDialog = false,
      ): Promise<ApiServiceResponse<ApiResponse>> => {
        return this.post<ApiResponse>('payment/dropin/keepAwake', {
          data: {...data},
          showNetworkErrorDialog,
        });
      },
    },
  };

  public userTokens = {
    zendeskIdentification: (
      data: ApiRequest = {},
      showNetworkErrorDialog = false,
    ): Promise<
      ApiServiceResponse<ApiResponseUserTokenZendeskIdentification>
    > => {
      return this.post('user_tokens/zendesk_identification', {
        data: {...data},
        showNetworkErrorDialog,
      });
    },
  };

  public user = {
    finishLogin: ({
      data: {...data},
      showNetworkErrorDialog = true,
    }: {
      data: ApiRequestFinishLogin;
      showNetworkErrorDialog?: boolean;
    }): Promise<ApiServiceResponse<ApiResponseFinishLogin>> => {
      return this.post('auth/finish', {
        data: {...data},
        showNetworkErrorDialog,
        includeLanguage: false,
      });
    },
  };
}
